import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import type {
  CompetitionRound,
  CreateRoundData,
  RoundType,
} from '../../types/competition-round';

interface NewRoundDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Creates it. Throws on failure, which leaves the dialog open and filled in. */
  onCreate: (data: CreateRoundData) => Promise<CompetitionRound>;
}

/**
 * A new round.
 *
 * Only three things are asked, because only three are decided this early: what
 * it is called, whether it is climbed on boulders or on a rope, and how long it
 * runs. When it actually starts is the day's business and is edited in the list
 * afterwards — and the length can still be changed by whoever presses go.
 *
 * **The kind cannot be changed later.** It is the record's class, and a round's
 * results are of a kind with it, so it is asked once, here, rather than offered
 * as an edit that would have to throw away everything under it.
 */
export default function NewRoundDialog({ open, onOpenChange, onCreate }: NewRoundDialogProps) {
  const { t } = useTranslation();
  const [name, setName] = useState('');
  const [roundType, setRoundType] = useState<RoundType>('Boulder');
  const [minutes, setMinutes] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setName('');
    setRoundType('Boulder');
    setMinutes('');
  }, [open]);

  const parsed = Number(minutes);
  // Blank is a real answer: a round with no duration runs until somebody closes
  // it by hand. Anything typed has to be a sane number of minutes.
  const blank = minutes.trim() === '';
  const validMinutes = blank || (Number.isInteger(parsed) && parsed > 0 && parsed <= 24 * 60);
  const valid = name.trim() !== '' && validMinutes;

  const handleCreate = async () => {
    if (!valid || saving) return;

    setSaving(true);
    try {
      await onCreate({
        round_type: roundType,
        name: name.trim(),
        duration_minutes: blank ? null : parsed,
      });
      onOpenChange(false);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t('gymAdmin.rounds.new')}</DialogTitle>
          <DialogDescription>{t('gymAdmin.rounds.newDescription')}</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="new-round-name">{t('gymAdmin.rounds.name')}</Label>
            <Input
              id="new-round-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={t('gymAdmin.rounds.namePlaceholder')}
              autoFocus
              data-testid="new-round-name"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="new-round-type">{t('gymAdmin.rounds.type')}</Label>
            <Select value={roundType} onValueChange={(value) => setRoundType(value as RoundType)}>
              <SelectTrigger id="new-round-type" data-testid="new-round-type">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Boulder">{t('gymAdmin.rounds.types.boulder')}</SelectItem>
                <SelectItem value="Lead">{t('gymAdmin.rounds.types.lead')}</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">{t('gymAdmin.rounds.typeHelp')}</p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="new-round-duration">{t('gymAdmin.rounds.duration')}</Label>
            <Input
              id="new-round-duration"
              type="number"
              inputMode="numeric"
              min={1}
              max={24 * 60}
              value={minutes}
              onChange={(e) => setMinutes(e.target.value)}
              placeholder={t('gymAdmin.rounds.noLimit')}
              data-testid="new-round-duration"
            />
            <p className="text-xs text-muted-foreground">{t('gymAdmin.rounds.durationHelp')}</p>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
            {t('common.cancel')}
          </Button>
          <Button onClick={handleCreate} disabled={!valid || saving} data-testid="create-round">
            {saving && <Loader2 className="mr-2 size-4 animate-spin" />}
            {t('gymAdmin.rounds.create')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
